import React from 'react';
import { Chart } from '../engine/chart';
import { DEG } from '../engine/ephemeris';
import { PLANETS, SIGN_GLYPHS, SIGN_ELEMENT } from '../data/constants';

interface Props {
  chart: Chart;
  size?: number;
  showAspects?: boolean;
}

const elementBg: Record<string, string> = {
  Fire: 'hsl(8 30% 12%)',
  Earth: 'hsl(95 20% 12%)',
  Air: 'hsl(220 25% 14%)',
  Water: 'hsl(200 25% 14%)',
};

export const ChartWheel: React.FC<Props> = ({ chart, size = 520, showAspects = true }) => {
  const cx = size / 2, cy = size / 2;
  const rOuter = size * 0.47;
  const rSigns = size * 0.42;
  const rTicksOuter = size * 0.415;
  const rTicksInner = size * 0.395;
  const rHouses = size * 0.32;
  const rPlanet = size * 0.37;
  const rAspect = size * 0.22;

  const ascRot = chart.asc;
  const toAngle = (lon: number) => -(lon - ascRot) + 180;
  const polar = (r: number, deg: number): [number, number] => {
    const rad = deg * DEG;
    return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
  };

  const signWedges: React.ReactNode[] = [];
  for (let i = 0; i < 12; i++) {
    const a1 = toAngle(i * 30);
    const a2 = toAngle((i + 1) * 30);
    const [x1, y1] = polar(rSigns, a1);
    const [x2, y2] = polar(rSigns, a2);
    const [x3, y3] = polar(rOuter, a2);
    const [x4, y4] = polar(rOuter, a1);
    const path = `M ${x1} ${y1} A ${rSigns} ${rSigns} 0 0 0 ${x2} ${y2} L ${x3} ${y3} A ${rOuter} ${rOuter} 0 0 1 ${x4} ${y4} Z`;
    const midAngle = toAngle(i * 30 + 15);
    const [gx, gy] = polar((rSigns + rOuter) / 2, midAngle);
    signWedges.push(
      <g key={i}>
        <path d={path} fill={elementBg[SIGN_ELEMENT[i]]} stroke="hsl(var(--border))" strokeWidth="0.5" />
        <text x={gx} y={gy} fill="hsl(var(--foreground))" fontSize={size * 0.035} textAnchor="middle" dominantBaseline="central" fontFamily="serif">
          {SIGN_GLYPHS[i]}
        </text>
      </g>,
    );
  }

  const houseLines: React.ReactNode[] = [];
  if (Array.isArray(chart.cusps)) {
    for (let h = 1; h <= 12; h++) {
      const cuspLon = chart.cusps[h];
      if (typeof cuspLon !== 'number') continue;
      const a = toAngle(cuspLon);
      // Cusp line spans from inner planet ring through the sign band so it
      // is unmistakably visible against the dark theme.
      const [x1, y1] = polar(rAspect, a);
      const [x2, y2] = polar(rSigns, a);
      const isAngular = h === 1 || h === 4 || h === 7 || h === 10;
      houseLines.push(
        <line
          key={`h${h}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={isAngular ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))'}
          strokeWidth={isAngular ? 1.6 : 0.9}
          opacity={isAngular ? 0.95 : 0.55}
        />,
      );
      // Numeric house label just inside the sign ring.
      const labelAngle = toAngle(cuspLon + 2);
      const [lx, ly] = polar(rHouses + size * 0.012, labelAngle);
      houseLines.push(
        <text
          key={`hl${h}`}
          x={lx}
          y={ly}
          fill="hsl(var(--muted-foreground))"
          fontSize={size * 0.018}
          textAnchor="middle"
          dominantBaseline="central"
          opacity={0.7}
        >
          {h}
        </text>,
      );
    }
  }

  // Axis labels (AS / DC / MC / IC) with arrowheads pointing inward at the
  // four cardinal cusps. Emphasis matches classic natal charts: AS/MC bold,
  // DC/IC muted.
  const axisLabels: React.ReactNode[] = [];
  if (Array.isArray(chart.cusps)) {
    const axes: Array<{ h: number; label: string; primary: boolean }> = [
      { h: 1, label: 'AS', primary: true },
      { h: 7, label: 'DC', primary: false },
      { h: 10, label: 'MC', primary: true },
      { h: 4, label: 'IC', primary: false },
    ];
    for (const { h, label, primary } of axes) {
      const cuspLon = chart.cusps[h];
      if (typeof cuspLon !== 'number') continue;
      const a = toAngle(cuspLon);
      const [tipX, tipY] = polar(rSigns + size * 0.005, a);
      const [tailX, tailY] = polar(rSigns + size * 0.035, a);
      const color = primary ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))';
      const [labelX, labelY] = polar(rOuter + size * 0.018, a);
      axisLabels.push(
        <g key={`axis-${label}`}>
          <line
            x1={tailX}
            y1={tailY}
            x2={tipX}
            y2={tipY}
            stroke={color}
            strokeWidth={primary ? 1.4 : 1}
            opacity={primary ? 0.95 : 0.7}
          />
          {/* Arrowhead — small triangle at the tip */}
          <circle cx={tipX} cy={tipY} r={size * 0.006} fill={color} opacity={primary ? 0.95 : 0.7} />
          <text
            x={labelX}
            y={labelY}
            fill={color}
            fontSize={size * 0.022}
            textAnchor="middle"
            dominantBaseline="central"
            fontWeight={primary ? 600 : 500}
            opacity={primary ? 1 : 0.85}
          >
            {label}
          </text>
        </g>,
      );
    }
  }

  // Degree-tick ring between the sign band and the inner wheel.
  // 360 minor ticks (1°), every 5° medium, every 10° long.
  const degreeTicks: React.ReactNode[] = [];
  for (let d = 0; d < 360; d++) {
    const a = toAngle(d);
    const isTen = d % 10 === 0;
    const isFive = d % 5 === 0;
    const inner = isTen ? rTicksInner - size * 0.012 : isFive ? rTicksInner - size * 0.006 : rTicksInner;
    const [x1, y1] = polar(rTicksOuter, a);
    const [x2, y2] = polar(inner, a);
    degreeTicks.push(
      <line
        key={`t${d}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="hsl(var(--muted-foreground))"
        strokeWidth={isTen ? 0.7 : 0.4}
        opacity={isTen ? 0.6 : isFive ? 0.4 : 0.25}
      />,
    );
  }

  const aspectLines: React.ReactNode[] = [];
  if (showAspects) {
    const normLon = (x: number) => ((x % 360) + 360) % 360;
    for (const asp of chart.aspects) {
      if (!asp.major) continue;
      const lon1 = chart.planets[asp.p1];
      const lon2 = chart.planets[asp.p2];
      if (!Number.isFinite(lon1) || !Number.isFinite(lon2)) continue;
      const [x1, y1] = polar(rAspect, toAngle(normLon(lon1)));
      const [x2, y2] = polar(rAspect, toAngle(normLon(lon2)));
      if (![x1, y1, x2, y2].every(Number.isFinite)) continue;
      aspectLines.push(
        <line key={`${asp.p1}-${asp.p2}`} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={asp.color} strokeWidth={0.8} opacity={0.55} />,
      );
    }
  }

  const planetEntries = Object.entries(chart.planets).sort((a, b) => a[1] - b[1]);
  const displayRadii: Record<string, number> = {};
  const clusterOffset = size * 0.022;
  for (const [n] of planetEntries) displayRadii[n] = rPlanet;
  for (let i = 0; i < planetEntries.length; i++) {
    const [, lon] = planetEntries[i];
    for (let j = i + 1; j < planetEntries.length; j++) {
      let diff = Math.abs(planetEntries[j][1] - lon);
      if (diff > 180) diff = 360 - diff;
      if (diff < 7) displayRadii[planetEntries[j][0]] = rPlanet - clusterOffset * ((j - i) % 3 + 1);
    }
  }

  const planetNodes: React.ReactNode[] = [];
  for (const [name, lon] of planetEntries) {
    const a = toAngle(lon);
    const [px, py] = polar(displayRadii[name], a);
    const def = PLANETS.find(p => p.name === name);
    if (!def) continue;
    const isFortune = name === 'Fortune';
    const degreeInSign = Math.floor(lon - Math.floor(lon / 30) * 30);
    // Degree label sits just outside the planet glyph along the same radial.
    const [dx, dy] = polar(displayRadii[name] + size * 0.04, a);
    planetNodes.push(
      <g key={name}>
        {!isFortune && (
          <circle cx={px} cy={py} r={size * 0.024} fill="hsl(var(--background))" stroke={def.color} strokeWidth="1" />
        )}
        <text x={px} y={py} fill={def.color} fontSize={size * 0.034} textAnchor="middle" dominantBaseline="central" fontFamily="serif">
          {def.glyph}
        </text>
        <text
          x={dx}
          y={dy}
          fill="hsl(var(--foreground))"
          fontSize={size * 0.018}
          textAnchor="middle"
          dominantBaseline="central"
          opacity={0.75}
          fontFamily="serif"
        >
          {degreeInSign}°
        </text>
      </g>,
    );
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block mx-auto">
      <circle cx={cx} cy={cy} r={rOuter} fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={rSigns} fill="none" stroke="hsl(var(--muted-foreground))" strokeWidth="0.6" />
      {signWedges}
      {degreeTicks}
      {houseLines}
      {axisLabels}
      {aspectLines}
      {planetNodes}
      <circle cx={cx} cy={cy} r={size * 0.006} fill="hsl(var(--foreground))" />
    </svg>
  );
};